import axios, { AxiosResponse } from 'axios';
import { DataItem } from './activities';
import { apiClient } from '../apiClient';
import { User } from '../users';
import { Site } from '../sites/sites';
import { UtilityVehicle } from '../materials/utilityVehicles';
import { Truck } from '../materials/trucks';

export interface Report {
    _id: string;
    userId: User;
    date: string;
    reportState: number;
    reportType: number;
    reportSubmitValidationTimeStamp?: string;
    validatedTimeStamp?: string;
    vehicleId?: UtilityVehicle;
    truckId?: Truck;
    userPrice?: number;
    userPriceType?: number;
    vehiclePrice?: number;
    vehiclePriceType?: number;
    extraHours?: number;
    isLongTrip?: boolean;
    workStartTime?: string;
    workEndTime?: string;
    overTimeValidatedBy?: User;
}

export interface ReportResponse {
    message: string;
    data: Report;
}

export interface ReportsResponse {
    message: string;
    data: Report[];
}

export interface GetReportsQueries {
    date?: string;
}

export const getReports = async (queries?: GetReportsQueries): Promise<ReportsResponse> => {
    try {
        const response: AxiosResponse<ReportsResponse> = await apiClient.get('/reports', {
            params: queries,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getReportById = async (id: string): Promise<ReportResponse> => {
    try {
        const response: AxiosResponse<ReportResponse> = await apiClient.get(`/reports/${id}`);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface UpdateReportBody {
    userId?: string;
    date?: string;
    reportState?: number;
    reportType?: number;
    reportSubmitValidationTimeStamp?: string;
    vehicleId?: string | null;
    truckId?: string | null;
    userPrice?: number | null;
    userPriceType?: number | null;
    vehiclePrice?: number | null;
    vehiclePriceType?: number | null;
    extraHours?: number;
    isLongTrip?: boolean;
    workStartTime?: string | null;
    workEndTime?: string | null;
}

export const updateReport = async (id: string, body: UpdateReportBody): Promise<ReportResponse> => {
    try {
        const response: AxiosResponse<ReportResponse> = await apiClient.patch(`/reports/${id}`, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface MarkAsAbsentReportBody {
    userId: string;
    date: string;
}

export const createOrUpdateAbsentReport = async (body: MarkAsAbsentReportBody): Promise<ReportResponse> => {
    try {
        const response: AxiosResponse<ReportResponse> = await apiClient.post('/reports/absent', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface MarkAsAbsentsReportsBody {
    userId: string;
    startDate: string;
    endDate: string;
}

export const createOrUpdateAbsentsReports = async (body: MarkAsAbsentsReportsBody): Promise<ReportResponse> => {
    try {
        const response: AxiosResponse<ReportResponse> = await apiClient.post('/reports/absents', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface BulkValidateReportsBody {
    reportIds: string[];
}

export const bulkValidateReports = async (body: BulkValidateReportsBody): Promise<ReportsResponse> => {
    try {
        const response: AxiosResponse<ReportsResponse> = await apiClient.patch('/reports/bulk-validate', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface BulkFillReportsBody {
    reportIds: string[];
}

export const bulkFillReports = async (body: BulkFillReportsBody): Promise<ReportsResponse> => {
    try {
        const response: AxiosResponse<ReportsResponse> = await apiClient.patch('/reports/bulk-fill', body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface MonthlyReport extends Report {
    sites: Site[];
}

export interface GetMonthlyReportParams {
    date: string;
}

export interface MonthlyReportsResponse {
    data: MonthlyReport[];
    message: string;
}

export const getMonthlyReports = async (params: GetMonthlyReportParams): Promise<MonthlyReportsResponse> => {
    try {
        const response: AxiosResponse<MonthlyReportsResponse> = await apiClient.get('/reports/monthly/' + params.date);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface PeriodReport {
    user: User;
    dailyReportByDate: {
        date: string;
        report: Report;
    }[];
}

export interface GetPeriodReportQueries {
    startDate: string;
    endDate: string;
}

export interface PeriodReportsResponse {
    data: PeriodReport[];
    message: string;
}

export const getPeriodReports = async (queries: GetPeriodReportQueries): Promise<PeriodReportsResponse> => {
    try {
        const response: AxiosResponse<PeriodReportsResponse> = await apiClient.get('/reports/daily', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
export interface ReportWithActivities extends Report {
    activities: DataItem[];
    vehicleId?: any;
    truckId?: any;
    reportDateString: string;
}

export interface ReportByDate {
    date: string;
    report: ReportWithActivities;
}

export interface UserWithReports {
    _id: string;
    user: User;
    reportsByDate: ReportByDate[];
}

export interface GetMonthlyReportsByUserParams {
    date: string;
}

export interface MonthlyReportsByUserResponse {
    data: UserWithReports[];
    message: string;
}

export const getMonthlyReportsByUser = async (params: GetMonthlyReportsByUserParams): Promise<MonthlyReportsByUserResponse> => {
    try {
        const response: AxiosResponse<MonthlyReportsByUserResponse> = await apiClient.get('/reports/monthly-by-user/' + params.date);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface CreateReportData {
    _id: string;
    userId: string;
    date: string;
    reportState: number;
    __v: number;
}

export interface CreateReportResponse {
    message: string;
    data: CreateReportData;
}

export interface CreateReportBody {
    userId: string;
    date: string;
    state: number;
}

export const createReportIfEmpty = async (data: CreateReportBody): Promise<CreateReportResponse> => {
    try {
        const response: AxiosResponse<CreateReportResponse> = await apiClient.post('/reports', data);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
